<script>
import { mapActions } from 'vuex';
import { mapGetters } from 'vuex';
import { Xhr } from 'tortilla';

// Constants
const GalleryApi = {
    OUR_MODELS: '/api/media-gallery/model',
    VIDEO_GALLERY: '/api/media-gallery/video',
    VIRTUAL_TOURS: '/api/media-gallery/virtual-tours',
    INSPIRATION_GALLERY: '/api/media-gallery/inspiration'
};

// Utilities

/**
 * Transforms a string to camel case
 *
 * @param {String} val - The string to transform
 * @returns {String} - The transformed string
 */
const toCamelCase = (val) => {
    return val.split('-').map((str, i) => {
        return i === 0 ? str : str.charAt(0).toUpperCase() + str.slice(1)
    }).join('');
};

/**
 * Transforms a string to uppercase snake case
 *
 * @param {String} val - The string to transform
 * @returns {String} - The transformed string
 */
const toUpperSnakeCase = (val) => {
    return val.toUpperCase().split('-').join('_');
};

export default {
    data: () => ({
        // Size of the image we want
        // as thumbnail on a card
        thumbnailSize: 'large',
        isLoading: false,
        isErrored: false,
        activeTab: undefined,
        previousTab: undefined,
        residences: [],
        selectedImages: [],
        selectedVideo: '',
        animateInLeft: 'media__tabcontent-item--active animated fadeInLeftBig',
        animateInRight: 'media__tabcontent-item--active animated fadeInRightBig',
        animateOutLeft: 'animated fadeOutLeftBig',
        animateOutRight: 'animated fadeOutRightBig',
        hashIndices: ['our-models', 'video-gallery', 'virtual-tours', 'inspiration-gallery'],
        galleryData: {
            ourModels: [],
            videoGallery: [],
            virtualTours: [],
            inspirationGallery: []
        },
        modalOpen: false,
        videoModalOpen: false,
        inspirationFilter: 'all',
        galleryTags: false
    }),
    computed: {
        previousIndex() {
            return this.hashIndices.indexOf(this.previousTab);
        },
        activeIndex() {
            return this.hashIndices.indexOf(this.activeTab);
        },
        animateInClass() {
            return this.activeIndex > this.previousIndex
                ? this.animateInRight
                : this.animateInLeft
        },
        animateOutClass() {
            return this.activeIndex > this.previousIndex
                ? this.animateOutLeft
                : this.animateOutRight;
        },
        showImageModal() {
            return this.modalOpen === 'modal-model-gallery' ? 'modal-model-gallery' : this.modalOpen === 'modal-model-inspiration' ? 'modal-model-inspiration' : false;
        }
    },
    created() {
        // Prevents initial scroll/jump if page is loaded
        // with a hash
        if (window.location.hash) {
            const to = window.setTimeout(() => {
                window.scrollTo(0, 0);
                window.clearTimeout(to);
            }, 200);
        }
    },
    mounted() {
        this.handleHashchange();
        window.addEventListener('hashchange', this.handleHashchange, false);
    },
    beforeDestroy() {
        window.removeEventListener('hashchange', this.handleHashchange);
    },
    methods: {
        ...mapActions({
            put: 'slick/put',
            videoPut: 'modal/put'
        }),

        /**
         * Preloads the first image of an array
         * for thumbnail use
         *
         * @param {Array} imageArray - Array of images
         * @returns {Promise} - Returns a promise
         */
        preloadImage(imageArray) {
            return new Promise(resolve => {
                // If the first image exists
                if (imageArray[0]) {
                    const img = new Image();
                    const handleLoaded = () => {
                        resolve();
                        img.removeEventListener('load', handleLoaded);
                    };
                    img.addEventListener('load', handleLoaded, false);
                    img.src = imageArray[0].image[this.thumbnailSize];

                // Otherwise resolve the promise
                } else {
                    resolve();
                }
            });
        },

        /**
         * Runs through an array of data and preloads each image
         *
         * @param {Array} - Array of objects containing `images` property
         * @returns {Promise} - Returns a promise
         */
        preloadImages(data) {
            return new Promise(resolve => {

                const numItems = data.length;
                let numPreloaded = 0;

                data.forEach(item => {
                    if (item.assets) {
                        this.preloadImage(item.assets).finally(() => {
                            numPreloaded++;
                            if (numPreloaded === numItems) {
                                resolve(data);
                            }
                        });
                    } else if (item.image) {
                        this.preloadImage(item.image).finally(() => {
                            numPreloaded++;
                            if (numPreloaded === numItems) {
                                resolve(data);
                            }
                        });
                    } else if (item.youtube_video_thumbnail) {
                        this.preloadImage(item.youtube_video_thumbnail).finally(() => {
                            numPreloaded++;
                            if (numPreloaded === numItems) {
                                resolve(data);
                            }
                        })
                    }
                });

                resolve(data);
            });
        },

        /**
         * Updates the data property `this.activeTab` and
         * replaces the hash in the url
         *
         * @param {String} tab - Tab/hash to set as active
         */
        updateTab(tab) {
            this.previousTab = this.activeTab;
            this.activeTab = tab;
            window.history.replaceState({}, '', `${window.location.pathname}#${tab}`);
        },

        /**
         * Gets the current hash of the url, updates the tabs
         * and if no content exists, loads it
         */
        handleHashchange() {
            const activeHash = window.location.hash || '#our-models';
            const activeTab = activeHash.slice(1);

            // Prevent default scrolling
            window.scrollTo(0, 0);

            if (!activeTab) {
                // If no tab is active, load the first tab
                this.updateTab('our-models');
            } else {
                // Otherwise set the tab to the current hash
                this.previousTab = this.activeTab;
                this.activeTab = activeTab;
            }
            if (this.galleryData[toCamelCase(activeTab)] && this.galleryData[toCamelCase(activeTab)].length === 0) {
                this.loadGalleryData(activeTab);
            }
        },

        /**
         * Loads the gallery data for the current tab/hash
         *
         * @todo - Store this with a timestamp in localstore so
         *         when a user returns to page within an hour(?) period
         *         the page loads immediately
         *
         * @param {String} tab - Tab (gallery category) to retrieve
         * @returns {Promise} - Returns a promise
         */
        loadGalleryData(tab) {
            this.isLoading = true;

            return Xhr.get(GalleryApi[toUpperSnakeCase(tab)]).then(response => {
                this.isErrored = false;

                // Preload images first, then assign data to `galleryData`
                this.preloadImages(response.data.gallery).finally(() => {
                    this.galleryData[toCamelCase(tab)] = response.data.gallery || [];
                    if (response.data.tags) {
                        this.galleryTags = response.data.tags;
                    }
                    this.isLoading = false;
                });
            }).catch(err => {
                this.isLoading = false;
                this.isErrored = true;
                console.error(`No data found for '${tab}'`, err);
            });
        },

        /**
         * Provides the tab content with the proper animation class
         *
         * @param {String} tab - The tab requesting its class
         * @returns {String} - The class
         */
        getAnimateClass(tab) {
            let tabClass = '';

            if (this.activeTab === tab) {
                tabClass = this.animateInClass;
            } else if (this.previousTab === tab) {
                tabClass = this.animateOutClass;
            }
            return tabClass;
        },

        /**
         * Opens the "Our Models" gallery for the clicked residence
         *
         * @param {String} gallery - The gallery to grab item from
         * @param {String} type - Type of modal ('gallery'|'video'|'interactive')
         * @param {Number} index - Index of selected gallery item
         */
        openModelModal(gallery, type, index) {
            if (type === 'gallery') {
                this.selectedImages = this.galleryData.ourModels[index].assets;
                this.$nextTick(function() {
                    this.put({key: 'modalImageIndex', value: 0});
                });
                this.$eventsBus.$emit('open-modal', ['modal-model-gallery', {
                    index: 0
                }]);
            } else if (type === 'inspiration') {
                this.selectedImages = [this.galleryData.inspirationGallery[index]];
                this.$nextTick(function() {
                    this.put({key: 'modalImageIndex', value: index});
                });
                this.$eventsBus.$emit('open-modal', ['modal-model-inspiration', {
                    index: index
                }]);
            } else {
                const urlKey = type === 'interactive' ? 'virtual_tour_url' : 'video_url';
                this.selectedVideo = this.galleryData[toCamelCase(gallery)][index][urlKey];
                this.$eventsBus.$emit('open-video-modal', [`modal-model-${type}`, {
                    video: this.selectedVideo
                }])
            }
            this.modalOpen = `modal-model-${type}`;
        },

        /**
         * Closes the modal
         */
        closeModal() {
            this.modalOpen = '';
        },

        /**
         * Set filter for inspiration gallery
         */
        setFilter(item) {
            this.inspirationFilter = item;
        },

        showWithCurrentFilter(item) {
            if (item) {
                if (this.inspirationFilter === 'all') {
                    return true;
                }
                for (let i = 0; i < item.tags.length; i++) {
                    if (item.tags[i].name == this.inspirationFilter) {
                        return true;
                    }
                }
            }
        },

        setGalleryFilterTarget(item) {
            const updatedTag = item.split(' ').join('-').toLowerCase();

            return `.gallery-filter-bar__${updatedTag}`;
        }
    }
};
</script>



// WEBPACK FOOTER //
// resources/components/apps/media-gallery.vue